import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useAuth } from './hooks/useAuth';
import MainLoginPage from './components/auth/MainLoginPage';
import LoginPage from './components/auth/LoginPage';
import DealerComingSoon from './components/auth/DealerComingSoon';
import ProfileQuickForm from './components/auth/ProfileQuickForm';
import { SecurityCheck } from './components/SecurityCheck';
import { AppProvider } from './context/AppContext';
import { NotificationProvider } from './context/NotificationContext';
import Dashboard from './pages/Dashboard';
import Scanner from './pages/Scanner';
import PaymentRequest from './pages/PaymentRequest';
import PointsRedeem from './pages/PointsRedeem';
import Transactions from './pages/Transactions';
import { KycGate } from './components/KycGate';
import { LocationPermissionGate } from './components/LocationPermissionGate';
import Profile from './pages/Profile';
import OnboardingKyc from './components/onboarding/OnboardingKyc';
import OnboardingBank from './components/onboarding/OnboardingBank';
import OnboardingPending from './components/onboarding/OnboardingPending';
import BottomNav from './components/BottomNav';
import Header from './components/Header';
import TermsAndConditions from './pages/TermsAndConditions';
import DealerWarningPopup from './components/DealerWarningPopup';

function App() {
  const { user, loading, needsProfileCompletion } = useAuth();
  const { i18n } = useTranslation();
  const [showDealerWarning, setShowDealerWarning] = useState(false);

  // Force show splash for at least 3 seconds on initial load
  const [splashActive, setSplashActive] = useState(true);
  useEffect(() => {
    const timer = setTimeout(() => setSplashActive(false), 3000);
    return () => clearTimeout(timer);
  }, []);

  // Track page views and show dealer warning popup
  useEffect(() => {
    if (!user || user.dealerCode || user.dealerId || loading || splashActive) {
      return; // Don't show if user has dealer or still loading
    }

    // Track browsing time - show popup after user has been browsing for 30 seconds
    const browseTimer = setTimeout(() => {
      // Check if popup was shown recently (within last 5 minutes)
      const lastShown = localStorage.getItem('dealerWarningLastShown');
      const now = Date.now();
      const fiveMinutes = 5 * 60 * 1000;

      if (!lastShown || (now - parseInt(lastShown, 10)) > fiveMinutes) {
        setShowDealerWarning(true);
        localStorage.setItem('dealerWarningLastShown', now.toString());
      }
    }, 30000); // Show after 30 seconds of browsing

    return () => clearTimeout(browseTimer);
  }, [user, loading, splashActive]);

  // Apply font based on language
  useEffect(() => {
    const currentLang = i18n.language || 'en';
    const root = document.documentElement;
    if (currentLang === 'bn') {
      document.body.style.fontFamily = "'Hind Siliguri', 'Poppins', sans-serif";
      root.setAttribute('data-lang', 'bn');
    } else {
      document.body.style.fontFamily = "'Poppins', sans-serif";
      root.setAttribute('data-lang', currentLang);
    }
  }, [i18n.language]);

  // Professional mobile app styles
  const appContainerStyle = {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 50%, #d1d5db 100%)',
    paddingTop: 'calc(env(safe-area-inset-top) + 68px)', // Add padding for fixed header (56px height + 12px padding)
    paddingBottom: 'env(safe-area-inset-bottom)',
    paddingLeft: 'env(safe-area-inset-left)',
    paddingRight: 'env(safe-area-inset-right)',
    overflowX: 'hidden' as const
    // fontFamily will inherit from body
  };

  const loadingContainerStyle = {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(135deg,rgb(248, 47, 47) 0%,rgba(250, 57, 22, 0.88) 100%)',
    fontFamily: "'Poppins', ui-sans-serif, system-ui, 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, 'Noto Sans'"
  };

  const loadingContentStyle = {
    textAlign: 'center' as const,
    color: 'white',
    padding: '32px'
  };

  const spinnerStyle = {
    width: '48px',
    height: '48px',
    border: '3px solid rgba(255, 255, 255, 0.3)',
    borderTop: '3px solid white',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
    margin: '0 auto 16px'
  };

  // Loading state (minimum 3s splash)
  if (loading || splashActive) {
    const isDesktop = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(min-width: 1024px)').matches;
    if (isDesktop) {
      // Old loading screen for desktop
      return (
        <div style={loadingContainerStyle}>
          <div style={loadingContentStyle}>
            <img
              src="/logo.png"
              alt="SCI Paints Logo"
              style={{
                height: '64px',
                width: 'auto',
                margin: '0 auto 24px',
                filter: 'brightness(0) invert(1)'
              }}
            />
            <div style={spinnerStyle}></div>
            <p style={{
              marginTop: '16px',
              color: 'rgba(255, 255, 255, 0.9)',
              fontSize: '16px',
              fontWeight: '500'
            }}>
              Loading BONDHU Painter...
            </p>
          </div>
        </div>
      );
    }

    // Mobile/tablet: full-screen SVG splash (no scroll, true full-bleed)
    return (
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          width: '100%',
          height: '100%',
          overflow: 'hidden',
          background: 'white',
          fontFamily:
            "'Poppins', ui-sans-serif, system-ui, 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, 'Noto Sans'",
          WebkitOverflowScrolling: 'auto',
          overscrollBehavior: 'none',
          touchAction: 'none'
        }}
      >
        <img
          src="/Front Page Main.svg"
          alt="SCI Paints Loading"
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            display: 'block',
            pointerEvents: 'none',
            userSelect: 'none'
          }}
        />
      </div>
    );
  }

  // If not authenticated, show main login page
  if (!user) {
    return (
      <Router>
        <Routes>
          <Route path="/" element={<MainLoginPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/dealer-coming-soon" element={<DealerComingSoon />} />
          <Route path="/terms" element={<TermsAndConditions />} />
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
        <Toaster
          position="top-center"
          toastOptions={{
            duration: 3000,
            style: {
              background: '#333',
              color: '#fff',
              borderRadius: '12px',
              border: '1px solid #ff6b35',
            },
          }}
        />
      </Router>
    );
  }

  // If authenticated but profile not complete, hard-lock to quick-profile without header/nav
  if (needsProfileCompletion) {
    return (
      <Router>
        <Routes>
          <Route path="/quick-profile" element={<ProfileQuickForm />} />
          <Route path="*" element={<Navigate to="/quick-profile" replace />} />
        </Routes>
        <Toaster
          position="top-center"
          toastOptions={{
            duration: 3000,
            style: {
              background: '#333',
              color: '#fff',
              borderRadius: '12px',
              border: '1px solid #ff6b35',
            },
          }}
        />
      </Router>
    );
  }

  // --- Onboarding gates: KYC + Bank + Pending ---
  if (user && user.kyc) {
    const missingBank =
      !user.accountNumber ||
      !user.ifscCode ||
      !user.accountHolderName ||
      !user.bankName;

    // 1) KYC SUBMITTED (waiting for review)
    if (user.kyc.status === 'submitted') {
      // If bank missing OR admin marked needsBankResubmit => force Bank page
      if (missingBank || user.kyc.needsBankResubmit) {
        return (
          <Router>
            <Routes>
              <Route path="/onboarding/kyc" element={<OnboardingKyc />} />
              <Route path="/onboarding/bank" element={<OnboardingBank />} />
              <Route path="*" element={<Navigate to="/onboarding/bank" replace />} />
            </Routes>
            <Toaster
              position="top-center"
              toastOptions={{
                duration: 3000,
                style: {
                  background: '#333',
                  color: '#fff',
                  borderRadius: '12px',
                  border: '1px solid #ff6b35',
                },
              }}
            />
          </Router>
        );
      }

      // Otherwise, KYC is submitted and bank is OK → Pending screen
      return (
        <Router>
          <Routes>
            <Route path="/onboarding/pending" element={<OnboardingPending />} />
            <Route path="*" element={<Navigate to="/onboarding/pending" replace />} />
          </Routes>
          <Toaster
            position="top-center"
            toastOptions={{
              duration: 3000,
              style: {
                background: '#333',
                color: '#fff',
                borderRadius: '12px',
                border: '1px solid #ff6b35',
              },
            }}
          />
        </Router>
      );
    }

    // 2) KYC NOT SUBMITTED or REJECTED → always go to KYC form
    if (user.kyc.status === 'not_submitted' || user.kyc.status === 'rejected') {
      return (
        <Router>
          <Routes>
            <Route path="/quick-profile" element={<ProfileQuickForm />} />
            <Route path="/onboarding/kyc" element={<OnboardingKyc />} />
            <Route path="*" element={<Navigate to="/onboarding/kyc" replace />} />
          </Routes>
          <Toaster
            position="top-center"
            toastOptions={{
              duration: 3000,
              style: {
                background: '#333',
                color: '#fff',
                borderRadius: '12px',
                border: '1px solid #ff6b35',
              },
            }}
          />
        </Router>
      );
    }

    // 3) KYC APPROVED but Bank missing / needs resubmit → Bank page
    if (user.kyc.status === 'approved' && (missingBank || user.kyc.needsBankResubmit)) {
      return (
        <Router>
          <Routes>
            <Route path="/onboarding/kyc" element={<OnboardingKyc />} />
            <Route path="/onboarding/bank" element={<OnboardingBank />} />
            <Route path="*" element={<Navigate to="/onboarding/bank" replace />} />
          </Routes>
          <Toaster
            position="top-center"
            toastOptions={{
              duration: 3000,
              style: {
                background: '#333',
                color: '#fff',
                borderRadius: '12px',
                border: '1px solid #ff6b35',
              },
            }}
          />
        </Router>
      );
    }
  } else if (user && !user.kyc) {
    // No KYC object at all → go to KYC form
    return (
      <Router>
        <Routes>
          <Route path="/quick-profile" element={<ProfileQuickForm />} />
          <Route path="/onboarding/kyc" element={<OnboardingKyc />} />
          <Route path="*" element={<Navigate to="/onboarding/kyc" replace />} />
        </Routes>
        <Toaster
          position="top-center"
          toastOptions={{
            duration: 3000,
            style: {
              background: '#333',
              color: '#fff',
              borderRadius: '12px',
              border: '1px solid #ff6b35',
            },
          }}
        />
      </Router>
    );
  }

  // If authenticated and profile complete, show main app with navigation
  return (
    <LocationPermissionGate>
      <SecurityCheck user={user}>
        <NotificationProvider>
          <AppProvider>
            <Router>
              <div style={appContainerStyle}>
                <Header />
                <Routes>
                  <Route path="/" element={<Navigate to="/dashboard" replace />} />
                  <Route path="/login" element={<Navigate to="/dashboard" replace />} />
                  <Route path="/dashboard" element={<Dashboard />} />
                  <Route path="/scanner" element={<KycGate user={user}><Scanner /></KycGate>} />
                  <Route path="/payment" element={<KycGate user={user}><PaymentRequest /></KycGate>} />
                  <Route path="/points-redeem" element={<KycGate user={user}><PointsRedeem /></KycGate>} />
                  <Route path="/transactions" element={<KycGate user={user}><Transactions /></KycGate>} />
                  <Route path="/profile" element={<Profile />} />
                  <Route path="/terms" element={<TermsAndConditions />} />
                  {/* If user is already unlocked, ensure /quick-profile bounces to dashboard */}
                  <Route path="/quick-profile" element={<Navigate to="/dashboard" replace />} />
                  <Route path="/dealer-coming-soon" element={<DealerComingSoon />} />
                  <Route path="*" element={<Navigate to="/dashboard" replace />} />
                </Routes>
                <BottomNav />
              </div>
              {showDealerWarning && (
                <DealerWarningPopup
                  onClose={() => {
                    setShowDealerWarning(false);
                  }}
                />
              )}
              <Toaster
                position="top-center"
                toastOptions={{
                  duration: 3000,
                  style: {
                    background: '#333',
                    color: '#fff',
                    borderRadius: '12px',
                    border: '1px solid #ff6b35',
                  },
                }}
              />
            </Router>
          </AppProvider>
        </NotificationProvider>
      </SecurityCheck>
    </LocationPermissionGate>
  );
}

export default App;